import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Backdrop } from "@mui/material";
import { useLocation, useNavigate, matchPath } from "react-router-dom";
import ROUTES from "../../router/routes.constants";
import FinConsentContainer from "./FinConsentContainer";
import FinFactorDashboardContainer from "./FinFactorDashboardContainer";
import FinFactorBankAccountsContainer from "./FinFactorBankAccountsContainer";
import FinFactorInsuranceContainer from "./FinFactorInsuranceContainer";
import InvestmentPortfolioContainer from "./InvestmentPortfolioContainer";
import BrokerListingContainer from "./BrokerListingContainer";
import BrokerDetailContainer from "./BrokerDetailContainer";
import FinStockDetailContainer from "./FinStockDetailContainer";
import FinNpsDetailContainer from "./FinNpsDetailContainer";
import Finfdrdcontainer from "./FinFDRDContainer";
import {
    useInitiateConsentMutation,
    useGetConsentsQuery,
    useLazyGetVendorConsentStatusQuery,
} from "../../store/api/finfactor.apislice";
import { isNative } from "../../utils/platform.utils";
import {
    openInWebView,
    closeInAppBrowser,
    addBrowserListeners,
    removeBrowserListeners,
} from "../../utils/browser.utils";
import { usePlanGate } from "../../hooks/usePlanGate";
import { useInvestGate } from "../../hooks/useInvestGate";

const POLLING_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 90;

const FinFactorContainer = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [isSignupFlow, setIsSignupFlow] = useState(() => {
        const stateJustSignedUp = location.state?.justSignedUp;
        const localJustSignedUp = localStorage.getItem("justSignedUp") === "true";
        return !!(stateJustSignedUp || localJustSignedUp);
    });

    useEffect(() => {
        if (isSignupFlow) {
            if (location.state?.justSignedUp) {
                navigate(location.pathname, { replace: true, state: { ...location.state, justSignedUp: undefined } });
            }
            localStorage.removeItem("justSignedUp");
        }
    }, [isSignupFlow, location.state, location.pathname, navigate]);
    const [initiateConsent, { isLoading: isInitiating }] = useInitiateConsentMutation();
    const { requirePlanSelection } = usePlanGate();
    const { guardInvest, isCheckingInvestGate } = useInvestGate();
    const [pollingInterval, setPollingInterval] = useState(0);
    const [consentHandles, setConsentHandles] = useState([]);

    const pollIntervalRef = useRef(null);
    const pollingWorkerRef = useRef(null);
    const audioContextRef = useRef(null);
    const consentHandlesRef = useRef([]);
    const isCheckingRef = useRef(false);
    const pollCountRef = useRef(0);
    const isConsentFlowActiveRef = useRef(false);

    const {
        data: consentsData,
        isLoading: isConsentsLoading,
        refetch: refetchConsents,
    } = useGetConsentsQuery(undefined, {
        refetchOnMountOrArgChange: true,
        refetchOnFocus: true,
        refetchOnReconnect: true,
    });

    const [pollVendorConsentStatus] = useLazyGetVendorConsentStatusQuery();

    const getNormalizedConsentStatus = useCallback((payload) => {
        const status =
            payload?.consentStatus ||
            payload?.status ||
            payload?.data?.consentStatus ||
            payload?.data?.status ||
            payload?.result?.consentStatus ||
            payload?.result?.status ||
            payload?.result?.data?.consentStatus ||
            payload?.result?.data?.status;

        return typeof status === "string" ? status.toUpperCase() : "";
    }, []);

    const isTerminalConsentStatus = useCallback(
        (payload) => {
            const normalized = getNormalizedConsentStatus(payload);
            return normalized === "APPROVED" || normalized === "REJECTED";
        },
        [getNormalizedConsentStatus],
    );

    const stopConsentPolling = useCallback(() => {
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
        if (pollingWorkerRef.current) {
            pollingWorkerRef.current.terminate();
            pollingWorkerRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {});
            audioContextRef.current = null;
        }
    }, []);

    const finishConsentFlow = useCallback(async () => {
        stopConsentPolling();
        removeBrowserListeners();
        isConsentFlowActiveRef.current = false;
        consentHandlesRef.current = [];
        pollCountRef.current = 0;

        await closeInAppBrowser().catch((err) =>
            console.error("Browser close error:", err),
        );

        refetchConsents();
        navigate(ROUTES.FINANCIAL_OVERVIEW);
    }, [navigate, refetchConsents, stopConsentPolling]);

    const checkConsentStatus = useCallback(async () => {
        if (isCheckingRef.current || consentHandlesRef.current.length === 0) {
            return;
        }

        isCheckingRef.current = true;
        pollCountRef.current += 1;
        console.log(
            `Consent status polling attempt ${pollCountRef.current} (FinFactor)`,
            consentHandlesRef.current,
        );

        try {
            const response = await pollVendorConsentStatus(
                consentHandlesRef.current,
            ).unwrap();

            const results = Array.isArray(response)
                ? response
                : [response];

            const terminalResult = results.find(isTerminalConsentStatus);

            if (terminalResult) {
                const finalStatus = getNormalizedConsentStatus(terminalResult);
                console.log(
                    `%c TERMINAL STATUS REACHED (FinFactor): ${finalStatus} `,
                    "background: #222; color: #bada55; font-size: 16px",
                );
                await finishConsentFlow();
                return;
            }

            if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                console.warn("Consent polling max attempts reached (FinFactor)");
                stopConsentPolling();
            }
        } catch (err) {
            console.error("Consent status polling error (FinFactor):", err);
            if (pollCountRef.current >= MAX_POLL_ATTEMPTS) {
                stopConsentPolling();
            }
        } finally {
            isCheckingRef.current = false;
        }
    }, [
        pollVendorConsentStatus,
        finishConsentFlow,
        isTerminalConsentStatus,
        getNormalizedConsentStatus,
        stopConsentPolling,
    ]);

    const startKeepAliveHeartbeats = useCallback(() => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (AudioCtx) {
                audioContextRef.current = new AudioCtx();
                const oscillator = audioContextRef.current.createOscillator();
                const gainNode = audioContextRef.current.createGain();
                oscillator.connect(gainNode);
                gainNode.connect(audioContextRef.current.destination);
                gainNode.gain.value = 0.001;
                oscillator.start();
            }
        } catch (e) {
            console.warn("Consent audio heartbeat failed (FinFactor)", e);
        }

        try {
            const workerBlob = new Blob(
                [`setInterval(() => { self.postMessage('tick'); }, 5000);`],
                { type: "application/javascript" },
            );
            pollingWorkerRef.current = new Worker(
                URL.createObjectURL(workerBlob),
            );
            pollingWorkerRef.current.onmessage = () => checkConsentStatus();
        } catch (e) {
            console.warn("Consent worker heartbeat failed (FinFactor)", e);
        }
    }, [checkConsentStatus]);

    const startConsentPolling = useCallback(() => {
        stopConsentPolling();
        pollCountRef.current = 0;
        startKeepAliveHeartbeats();

        pollIntervalRef.current = setInterval(
            checkConsentStatus,
            POLLING_INTERVAL_MS,
        );

        setTimeout(checkConsentStatus, 500);
    }, [checkConsentStatus, stopConsentPolling, startKeepAliveHeartbeats]);

    const closeConsentBrowserOnly = useCallback(async () => {
        removeBrowserListeners();
        await closeInAppBrowser().catch((err) =>
            console.error("Browser close error:", err),
        );
    }, []);

    const handleBrowserClosed = useCallback(() => {
        if (!isConsentFlowActiveRef.current) return;
        console.log(
            "Browser closed during consent flow (FinFactor). Polling continues until terminal status.",
        );
        removeBrowserListeners();
        checkConsentStatus();
    }, [checkConsentStatus]);

    const handleBrowserNavigationCompleted = useCallback(
        (data) => {
            const url = data?.url || "";
            console.log("InAppBrowser Navigation (FinFactor):", url);

            const isTerminalRedirectUrl =
                url.includes("account-aggregator-service.inxits.com") ||
                url.includes("inxits-customer-app");

            if (isTerminalRedirectUrl) {
                console.log(
                    "Terminal URL reached (FinFactor). Closing browser; polling continues until APPROVED/REJECTED.",
                );
                closeConsentBrowserOnly();
                checkConsentStatus();
            }
        },
        [closeConsentBrowserOnly, checkConsentStatus],
    );

    const startNativeConsentJourney = useCallback(
        async (redirectUrl, handles) => {
            consentHandlesRef.current = handles;
            isConsentFlowActiveRef.current = true;

            await addBrowserListeners(
                handleBrowserClosed,
                () => checkConsentStatus(),
                handleBrowserNavigationCompleted,
            );

            openInWebView(redirectUrl).catch((err) =>
                console.error("Failed to open consent webview:", err),
            );

            startConsentPolling();
        },
        [
            handleBrowserClosed,
            handleBrowserNavigationCompleted,
            checkConsentStatus,
            startConsentPolling,
        ],
    );

    useEffect(() => {
        return () => {
            stopConsentPolling();
            if (isNative()) {
                removeBrowserListeners();
            }
        };
    }, [stopConsentPolling]);

    const startConsentJourney = useCallback(async () => {
        try {
            const payload = isNative() ? { isMobileJourney: true } : {};
            const response = await initiateConsent(payload).unwrap();
            console.log("Consent Initiation Response:", response);

            const redirectUrl = response?.result?.url || response?.result;
            console.log("Redirect URL:", redirectUrl);

            if (redirectUrl && typeof redirectUrl === "string") {
                if (isNative()) {
                    const handles = response?.result?.consentHandles
                        ? Object.values(response.result.consentHandles)
                        : [];

                    if (handles.length > 0) {
                        await startNativeConsentJourney(redirectUrl, handles);
                    } else {
                        console.warn("No consent handles received for polling");
                    }
                } else {
                    window.location.href = redirectUrl;
                }
            } else {
                console.warn("No redirect URL received.");
            }
        } catch (error) {
            isConsentFlowActiveRef.current = false;
            stopConsentPolling();
            console.error("Error initiating consent:", error);
        }
    }, [initiateConsent, startNativeConsentJourney, stopConsentPolling]);

    // Linking external accounts is gated exactly like the Invest button: the
    // plan gate first (plan chosen / paid / signed / risk profiled), then the
    // full invest gate, so an un-onboarded customer is never handed off to the
    // account-aggregator consent journey.
    const handleConsent = useCallback(async () => {
        if (requirePlanSelection()) return;

        await guardInvest(startConsentJourney);
    }, [requirePlanSelection, guardInvest, startConsentJourney]);

    const renderView = () => {
        if (isConsentsLoading && !isSignupFlow) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "80vh",
                    }}
                >
                    <CircularProgress />
                </Box>
            );
        }

        const totalConsents = consentsData?.totalConsents || 0;

        if (totalConsents === 0) {
            return <FinConsentContainer onConsent={handleConsent} />;
        }

        const pathname = location.pathname;

        if (matchPath(ROUTES.FINFACTOR_BANKING + "/*", pathname)) {
            return <FinFactorBankAccountsContainer />;
        }

        if (matchPath(ROUTES.FINFACTOR_INSURANCE + "/*", pathname)) {
            return <FinFactorInsuranceContainer />;
        }

        if (
            matchPath(ROUTES.INVESTMENT_PORTFOLIO_BROKER, pathname) ||
            matchPath(ROUTES.INVESTMENT_PORTFOLIO_ORDER, pathname)
        ) {
            if (
                pathname.includes("/stocks") ||
                pathname.includes("/etf") ||
                pathname.includes("/invit") ||
                pathname.includes("/reit")
            ) {
                return <FinStockDetailContainer />;
            }
            if (pathname.includes("/fixed-deposit")) {
                return <Finfdrdcontainer />;
            }
            return <BrokerDetailContainer />;
        }

        if (matchPath(ROUTES.INVESTMENT_PORTFOLIO_DETAILS, pathname)) {
            if (pathname.endsWith("/nps")) {
                return <FinNpsDetailContainer />;
            }
            if (pathname.endsWith("/fixed-deposit")) {
                return <Finfdrdcontainer />;
            }
            return <BrokerListingContainer />;
        }

        if (matchPath(ROUTES.INVESTMENT_PORTFOLIO, pathname)) {
            return <InvestmentPortfolioContainer />;
        }

        return <FinFactorDashboardContainer />;
    };

    return (
        <Box
            sx={{
                backgroundColor: "#0F172A",
                minHeight: "100vh",
                position: "relative",
            }}
        >
            <Backdrop
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
                open={isInitiating || isCheckingInvestGate}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            {renderView()}
        </Box>
    );
};

export default FinFactorContainer;
